/**
 * Sticky ATC — Sprint 32
 * Shared JS for mobile drawer + desktop bar
 * Uses IntersectionObserver (no scroll events)
 */

// === MOBILE ===
class StickyAtcMobile extends HTMLElement {
  constructor() {
    super();
    this.drawer = this.querySelector('.sticky-atc-mobile__drawer');
    this.trigger = this.querySelector('.sticky-atc-mobile__trigger');
    this.closeButtons = this.querySelectorAll('[data-close-drawer]');
    this.addButton = this.querySelector('[data-sticky-add-to-cart]');
    this.buyNowButton = this.querySelector('[data-sticky-buy-now]');
    this.scrollButton = this.querySelector('[data-scroll-to-atc]');
    this.quantityInput = this.querySelector('[data-sticky-quantity]');
    this.variantSelects = this.querySelectorAll('[data-option-position]');
  }

  connectedCallback() {
    this.initObserver();
    this.initDrawer();
    this.initVariantSync();
    this.initQuantity();
    this.initAddToCart();
    this.initBuyNow();
    this.initScrollToAtc();
  }

  initObserver() {
    const atcButton = document.querySelector('.product-form__submit, [name="add"], .product-form [type="submit"], .btn-add-to-cart');
    if (!atcButton) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        const shouldShow = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.classList.toggle('is-visible', shouldShow);
        this.setAttribute('aria-hidden', String(!shouldShow));
      },
      { threshold: 0, rootMargin: '0px' }
    );
    this.observer.observe(atcButton);
  }

  initDrawer() {
    if (!this.drawer || !this.trigger) return;

    this.trigger.addEventListener('click', () => this.openDrawer());
    this.closeButtons.forEach(btn =>
      btn.addEventListener('click', () => this.closeDrawer())
    );

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.closeDrawer();
      }
    });
  }

  openDrawer() {
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.trigger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  initVariantSync() {
    if (!this.variantSelects.length) return;

    const formContainer = document.querySelector('.product-form');
    if (formContainer) {
      formContainer.addEventListener('change', () => this.syncFromMain());
    }

    this.variantSelects.forEach(select => {
      select.addEventListener('change', () => this.syncToMain());
    });
  }

  syncFromMain() {
    const mainSelects = document.querySelectorAll('.product-form select, .product-form__input select');
    mainSelects.forEach((mainSelect, i) => {
      if (this.variantSelects[i]) {
        this.variantSelects[i].value = mainSelect.value;
      }
    });
    this.updateVariant();
  }

  syncToMain() {
    const mainSelects = document.querySelectorAll('.product-form select, .product-form__input select');
    this.variantSelects.forEach((stickySelect, i) => {
      if (mainSelects[i]) {
        mainSelects[i].value = stickySelect.value;
        mainSelects[i].dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    this.updateVariant();
  }

  updateVariant() {
    const selectedOptions = [];
    this.variantSelects.forEach(select => selectedOptions.push(select.value));

    const productData = document.querySelector('[data-product-json]');
    if (!productData) return;

    try {
      const product = JSON.parse(productData.textContent);
      const variant = product.variants.find(v =>
        v.options.every((opt, i) => opt === selectedOptions[i])
      );

      if (variant && this.addButton) {
        this.addButton.dataset.variantId = variant.id;
        this.addButton.disabled = !variant.available;

        const addText = this.addButton.querySelector('[data-add-text]');
        const soldOutText = this.addButton.querySelector('[data-sold-out-text]');
        if (addText && soldOutText) {
          addText.hidden = !variant.available;
          soldOutText.hidden = variant.available;
        }
      }
    } catch (e) {
      console.warn('Sticky ATC: Could not parse product JSON', e);
    }
  }

  initQuantity() {
    if (!this.quantityInput) return;
    const minus = this.querySelector('[data-quantity-minus]');
    const plus = this.querySelector('[data-quantity-plus]');

    minus?.addEventListener('click', () => {
      const val = parseInt(this.quantityInput.value) || 1;
      this.quantityInput.value = Math.max(1, val - 1);
    });

    plus?.addEventListener('click', () => {
      const val = parseInt(this.quantityInput.value) || 1;
      const max = parseInt(this.quantityInput.max) || 99;
      this.quantityInput.value = Math.min(max, val + 1);
    });
  }

  initAddToCart() {
    if (!this.addButton) return;
    this.addButton.addEventListener('click', async () => {
      const variantId = this.addButton.dataset.variantId;
      const quantity = this.quantityInput ? parseInt(this.quantityInput.value) : 1;
      if (!variantId || this.addButton.disabled) return;

      const addText = this.addButton.querySelector('[data-add-text]');
      const loadingText = this.addButton.querySelector('[data-loading-text]');
      if (addText) addText.hidden = true;
      if (loadingText) loadingText.hidden = false;

      try {
        await window.LeanCart.addItem({ id: parseInt(variantId), quantity }, true);
        this.closeDrawer();
      } catch (e) {
        console.error('Sticky ATC: Add to cart error', e);
      } finally {
        if (addText) addText.hidden = false;
        if (loadingText) loadingText.hidden = true;
      }
    });
  }

  initBuyNow() {
    if (!this.buyNowButton) return;
    this.buyNowButton.addEventListener('click', async () => {
      const variantId = this.addButton?.dataset.variantId;
      const quantity = this.quantityInput ? parseInt(this.quantityInput.value) : 1;
      if (!variantId) return;

      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId), quantity })
        });
        window.location.href = '/checkout';
      } catch (e) {
        console.error('Sticky ATC: Buy now error', e);
      }
    });
  }

  initScrollToAtc() {
    if (!this.scrollButton) return;
    this.scrollButton.addEventListener('click', () => {
      const atcButton = document.querySelector('.product-form__submit, [name="add"], .btn-add-to-cart');
      atcButton?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('sticky-atc-mobile')) {
  customElements.define('sticky-atc-mobile', StickyAtcMobile);
}


// === DESKTOP ===
class StickyAtcDesktop extends HTMLElement {
  constructor() {
    super();
    this.addButton = this.querySelector('[data-sticky-add-to-cart]');
    this.buyNowButton = this.querySelector('[data-sticky-buy-now]');
    this.quantityInput = this.querySelector('[data-sticky-quantity]');
    this.variantSelects = this.querySelectorAll('[data-option-position]');
  }

  connectedCallback() {
    this.initObserver();
    this.initVariantSync();
    this.initQuantity();
    this.initAddToCart();
    this.initBuyNow();
  }

  initObserver() {
    const atcButton = document.querySelector('.product-form__submit, [name="add"], .product-form [type="submit"], .btn-add-to-cart');
    if (!atcButton) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        const shouldShow = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.classList.toggle('is-visible', shouldShow);
        this.setAttribute('aria-hidden', String(!shouldShow));
      },
      { threshold: 0, rootMargin: '0px' }
    );
    this.observer.observe(atcButton);
  }

  initVariantSync() {
    if (!this.variantSelects.length) return;
    const formContainer = document.querySelector('.product-form');
    if (formContainer) {
      formContainer.addEventListener('change', () => this.syncFromMain());
    }
    this.variantSelects.forEach(select => {
      select.addEventListener('change', () => this.syncToMain());
    });
  }

  syncFromMain() {
    const mainSelects = document.querySelectorAll('.product-form select');
    mainSelects.forEach((mainSelect, i) => {
      if (this.variantSelects[i]) this.variantSelects[i].value = mainSelect.value;
    });
    this.updateVariant();
  }

  syncToMain() {
    const mainSelects = document.querySelectorAll('.product-form select');
    this.variantSelects.forEach((stickySelect, i) => {
      if (mainSelects[i]) {
        mainSelects[i].value = stickySelect.value;
        mainSelects[i].dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    this.updateVariant();
  }

  updateVariant() {
    const selectedOptions = [];
    this.variantSelects.forEach(select => selectedOptions.push(select.value));
    const productData = document.querySelector('[data-product-json]');
    if (!productData) return;
    try {
      const product = JSON.parse(productData.textContent);
      const variant = product.variants.find(v =>
        v.options.every((opt, i) => opt === selectedOptions[i])
      );
      if (variant && this.addButton) {
        this.addButton.dataset.variantId = variant.id;
        this.addButton.disabled = !variant.available;
        const addText = this.addButton.querySelector('[data-add-text]');
        const soldOutText = this.addButton.querySelector('[data-sold-out-text]');
        if (addText && soldOutText) {
          addText.hidden = !variant.available;
          soldOutText.hidden = variant.available;
        }
      }
    } catch (e) { /* silent */ }
  }

  initQuantity() {
    if (!this.quantityInput) return;
    const minus = this.querySelector('[data-quantity-minus]');
    const plus = this.querySelector('[data-quantity-plus]');
    minus?.addEventListener('click', () => {
      this.quantityInput.value = Math.max(1, (parseInt(this.quantityInput.value) || 1) - 1);
    });
    plus?.addEventListener('click', () => {
      this.quantityInput.value = Math.min(99, (parseInt(this.quantityInput.value) || 1) + 1);
    });
  }

  initAddToCart() {
    if (!this.addButton) return;
    this.addButton.addEventListener('click', async () => {
      const variantId = this.addButton.dataset.variantId;
      const quantity = this.quantityInput ? parseInt(this.quantityInput.value) : 1;
      if (!variantId || this.addButton.disabled) return;
      try {
        await window.LeanCart.addItem({ id: parseInt(variantId), quantity }, true);
      } catch (e) {
        console.error('Sticky ATC Desktop: Error', e);
      }
    });
  }

  initBuyNow() {
    if (!this.buyNowButton) return;
    this.buyNowButton.addEventListener('click', async () => {
      const variantId = this.addButton?.dataset.variantId;
      const quantity = this.quantityInput ? parseInt(this.quantityInput.value) : 1;
      if (!variantId) return;
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId), quantity })
        });
        window.location.href = '/checkout';
      } catch (e) { console.error(e); }
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('sticky-atc-desktop')) {
  customElements.define('sticky-atc-desktop', StickyAtcDesktop);
}
